/**
 * FullPageContentContext keeps track of whether we should render the content
 * in a full page view without a sidebar or headers. The reason this context
 * exists is that parts of the full page view must be rendered at the top
 * of the React component hierarchy, but parts are deep down in the hierarchy,
 * and we want to be able to enter and exit full page mode with UI components
 * deep within the hierarchy.
 *
 * The motivating use case for this mode is the in-browser Python coding
 * environment used in the Computing domain. We want users to be able to
 * maximize the size of the code editor and output panel, so they can see more
 * of their code and focus on programming. However, it could potentially be
 * useful for other kinds of content as well.
 *
 * Note: we assume that full page content does not make sense at mobile viewport
 * sizes, so we have code wherever the provider is created to disable
 * full page mode on small screens. If you try to enable it on a small screen,
 * it will immediately be disabled.
 */
import * as React from "react";

import {Errors, KAError} from "@khan/logging";

type FullPageContentContextType = {
    isContentFullPage: boolean;
    setIsContentFullPage: (isContentFullPage: boolean) => void;
};

const FullPageContentContext: React.Context<FullPageContentContextType> =
    React.createContext<FullPageContentContextType>({
        isContentFullPage: false,
        setIsContentFullPage: () => {
            // The component setting up the Provider must replace this function
            // with one that handles setter calls.
            throw new KAError(
                "setIsContentFullPage must be used within a FullPageContentContext.Provider",
                Errors.Internal,
            );
        },
    });
FullPageContentContext.displayName = "FullPageContentContext";

export default FullPageContentContext;
